/**
 * Product Supply Component
 * 
 * Displays supply chain metrics with a vertical bar chart:
 * - Product orders → In Transit → Received → In Stock
 * - Lead time
 * - COGS (Cost of Goods Sold)
 */

import React from 'react';
import { Tag, Tooltip } from 'antd';
import {
  DropboxOutlined,
  ShoppingCartOutlined,
  ClockCircleOutlined,
  DollarOutlined
} from '@ant-design/icons';
import { BaseReportingComponent, ComponentData, ReportingComponentProps } from './BaseReportingComponent';
import { ProcessStepsChart, ProcessStep } from './ProcessStepsChart';
import { fetchProductDomainDashboardComponent } from '../../../services/bridgeSessionDataAdapter';
import { isDriveFlowReportingEnabled } from '../../../services/driveFlowAdapter';
import styles from './ProductSupplyComponent.module.css';

interface ProductSupplyComponentProps extends Omit<ReportingComponentProps, 'componentType'> {
  /** Whether to use compact view */
  compact?: boolean;
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const formatDuration = (days: number): string => {
  if (days < 1) return '< 1 day';
  if (days === 1) return '1 day';
  if (days < 7) return `${days} days`;
  if (days < 30) return `${Math.round(days / 7)} weeks`;
  return `${Math.round(days / 30)} months`;
};

const getSupplySteps = (data: Record<string, any>): ProcessStep[] => {
  // Check for custom supply steps first
  if (data.supply_steps && Array.isArray(data.supply_steps)) {
    return data.supply_steps.map((step: any, idx: number) => ({
      id: step.id || `step-${idx}`,
      name: step.name || 'Step',
      count: step.count || 0,
      color: '#10b981'
    }));
  }

  // Default supply chain steps
  return [
    {
      id: 'orders',
      name: 'Orders',
      count: data.product_orders ?? data.total_orders ?? 0,
      color: '#34d399'
    },
    {
      id: 'in_transit',
      name: 'In Transit',
      count: data.in_transit ?? data.supply_in_transit ?? 0,
      color: '#10b981'
    },
    {
      id: 'received',
      name: 'Received',
      count: data.received ?? data.supply_received ?? 0,
      color: '#059669'
    },
    {
      id: 'in_stock',
      name: 'In Stock',
      count: data.in_stock ?? data.inventory_level ?? 0,
      color: '#047857'
    }
  ];
};

export const ProductSupplyComponent: React.FC<ProductSupplyComponentProps> = ({
  compact = false,
  ...props
}) => {
  const renderContent = (data: ComponentData, loading: boolean) => {
    const steps = getSupplySteps(data.data);
    
    const productOrders = data.data.product_orders ?? data.data.total_orders ?? 0;
    const leadTime = data.data.lead_time ?? data.data.supply_lead_time ?? 0;
    const cogs = data.data.cogs ?? data.data.total_cogs ?? 0;

    return (
      <div className={`${styles.productSupply} ${compact ? styles.compact : ''}`}>
        {!compact && (
          <div className={styles.header}>
            <h4 className={styles.title}>
              <DropboxOutlined /> Supply Chain
            </h4>
          </div>
        )}

        {/* Bar Chart */}
        <div className={styles.chartWrapper}>
          <ProcessStepsChart
            steps={steps}
            height={compact ? 140 : 180}
            barColor="#10b981"
            showLabels={true}
            showConversionRates={!compact}
            compact={compact}
          />
        </div>

        {/* Key Metrics */}
        <div className={styles.keyMetrics}>
          <Tooltip title="Total product orders">
            <div className={styles.keyMetric}>
              <ShoppingCartOutlined className={styles.metricIcon} />
              <div className={styles.metricContent}>
                <span className={styles.metricLabel}>Orders</span>
                <span className={styles.metricValue}>{productOrders}</span>
              </div>
            </div>
          </Tooltip>
          
          <Tooltip title="Average time from order to delivery">
            <div className={styles.keyMetric}>
              <ClockCircleOutlined className={styles.metricIcon} />
              <div className={styles.metricContent}>
                <span className={styles.metricLabel}>Lead Time</span>
                <span className={styles.metricValue}>{formatDuration(leadTime)}</span>
              </div>
            </div>
          </Tooltip>
          
          <Tooltip title="Cost of Goods Sold">
            <div className={styles.keyMetric}>
              <DollarOutlined className={styles.metricIcon} />
              <div className={styles.metricContent}>
                <span className={styles.metricLabel}>COGS</span>
                <span className={styles.metricValue} style={{ color: '#ef4444' }}>
                  {formatCurrency(cogs)}
                </span>
              </div>
            </div>
          </Tooltip>
        </div>
      </div>
    );
  };

  const customDataFetcher = isDriveFlowReportingEnabled()
    ? (id: string) => fetchProductDomainDashboardComponent(id, 'product_supply')
    : undefined;

  return (
    <BaseReportingComponent
      {...props}
      componentType="product_supply"
      showControls={!compact}
      customDataFetcher={customDataFetcher}
    >
      {renderContent}
    </BaseReportingComponent>
  );
};

export default ProductSupplyComponent;
